import { LineInfoRow, MachineDetailRow } from '../api';
import { UT_TYPES } from '../constants';
import { AnalysisRow, UtCellData, UtKey } from '../types';
import { sortByProcessGroup } from './sortRows';

interface MachineGroup {
  process: string;
  eqGroup: string;
  subProcess: string;
  line: string;
  machine: string;
  main: string;
  rows: MachineDetailRow[];
}

interface AggregatedEntry {
  process: string;
  eqGroup: string;
  subProcess: string;
  line: string;
  machine: string;
  main: string;
  lineQty: number;
  values: Record<
    UtKey,
    { usage: number; unit: number; qty: number; usagePerLine: number; qtyPerLine: number }
  >;
}

export interface AnalyzeParams {
  /** 비교 순서(V1..V3)대로 나열된 UTM_ID */
  utmIds: string[];
  /** 여러 utmId가 섞인 Machine 상세 전체 */
  rows: MachineDetailRow[];
  /** 여러 utmId가 섞인 라인정보 전체 (1라인별 비교 모드에서만 사용) */
  lineInfo: LineInfoRow[];
  perLine: boolean;
}

export interface AnalyzeResult {
  rows: AnalysisRow[];
  isPerLineMode: boolean;
  /** 1라인별 비교 모드에서 라인정보에 매칭되지 않은 설비군 (라인수량=1 처리됨) */
  unmatchedEqGroups: string[];
}

/**
 * perLine 모드에서 Main 값 표기가 버전 간 다를 수 있음 (예: "#1" vs "1")
 * → 비교/매칭용 정규화 키는 숫자/문자만 남기고 특수기호(#, 공백 등) 제거해서 사용
 */
function normalizeMain(s: unknown): string {
  return String(s || '')
    .trim()
    .replace(/[#\s]/g, '')
    .toUpperCase();
}

/** 라인정보에서 { 설비군명: 라인수량 } 맵 생성 (특정 utmId 기준) */
function buildEqGroupLineQtyMap(lineInfo: LineInfoRow[], utmId: string): Record<string, number> {
  const map: Record<string, number> = {};
  for (const r of lineInfo) {
    if (r.utmId !== utmId) continue;
    if (!r.eqclNm || r.eqpPrdnLnQty <= 0) continue;
    map[r.eqclNm] = r.eqpPrdnLnQty;
  }
  return map;
}

/** Machine+Line(또는 Main, perLine 모드) 조합별로 행을 수집 (특정 utmId 기준) */
function buildMap(
  rows: MachineDetailRow[],
  utmId: string,
  isPerLineMode: boolean
): Record<string, MachineGroup> {
  const map: Record<string, MachineGroup> = {};
  for (const r of rows) {
    if (r.utmId !== utmId) continue;
    const machine = (r.machEqpNm || '').trim();
    if (!machine) continue;
    const process = r.prdnProcNm || '';
    const eqGroup = r.eqclNm || '';
    const subProcess = r.dtalProcNm || '';
    const line = r.prdnLnNm || '';
    const main = r.eqpMainNm || '';
    const mainKey = isPerLineMode ? normalizeMain(main) : main;
    const key = isPerLineMode ? `${machine}||${mainKey}` : `${machine}||${line}`;
    if (!map[key]) map[key] = { process, eqGroup, subProcess, line, machine, main, rows: [] };
    map[key].rows.push(r);
  }
  return map;
}

/** Machine(+Main) 단위로 집계 */
function aggregateMachine(
  map: Record<string, MachineGroup>,
  lineQtyMap: Record<string, number>,
  isPerLineMode: boolean
): Record<string, AggregatedEntry> {
  const result: Record<string, AggregatedEntry> = {};
  for (const [key, info] of Object.entries(map)) {
    // 라인정보에서 설비군 매칭 → 라인수량. 매칭 없으면 1로 처리(나누지 않음)
    const lineQty = isPerLineMode ? lineQtyMap[info.eqGroup] || 1 : 1;
    const values = {} as AggregatedEntry['values'];
    for (const ut of UT_TYPES) {
      let totalUsage = 0;
      let unitUsage = 0;
      let qty = 0;
      let unitCount = 0;
      for (const r of info.rows) {
        const v = r.values[ut.key];
        totalUsage += v.usage;
        if (v.unit > 0) {
          unitUsage += v.unit;
          unitCount++;
        }
        if (v.qty > qty) qty = v.qty;
      }
      values[ut.key] = {
        usage: totalUsage,
        unit: unitCount > 0 ? unitUsage / unitCount : 0,
        qty,
        usagePerLine: totalUsage / lineQty,
        qtyPerLine: qty / lineQty,
      };
    }
    result[key] = {
      process: info.process,
      eqGroup: info.eqGroup,
      subProcess: info.subProcess,
      line: info.line,
      machine: info.machine,
      main: info.main,
      lineQty,
      values,
    };
  }
  return result;
}

export function analyze({ utmIds, rows, lineInfo, perLine }: AnalyzeParams): AnalyzeResult {
  const isPerLineMode = perLine && lineInfo.length > 0;

  const lineQtyMaps = utmIds.map((id) =>
    isPerLineMode ? buildEqGroupLineQtyMap(lineInfo, id) : {}
  );
  const aggs = utmIds.map((id, i) =>
    aggregateMachine(buildMap(rows, id, isPerLineMode), lineQtyMaps[i], isPerLineMode)
  );

  /*
    여기서는 키(`Machine||라인`) 기준으로만 훑는다 — 화면·산출물의 행 순서는
    utils/sortRows.ts의 sortAnalysisRows()가 정한다(공정은 공통코드 PRDN_PROC_CD 순서).
  */
  const allKeys = [...new Set(aggs.flatMap((a) => Object.keys(a)))].sort();

  // Machine별 총 사용량 차이(마지막-첫번째) 합계(라인 합산) — 변화 없으면 전체 숨김 판단용
  // perLine 모드에서는 "라인당 사용량" 차이를 기준으로 판단
  const machineTotalDiff: Record<string, Record<UtKey, number>> = {};
  for (const key of allKeys) {
    const entries = aggs.map((a) => a[key]);
    const machine = entries.find((e) => e)?.machine || key.split('||')[0];
    const groupKey = isPerLineMode ? key : machine;
    if (!machineTotalDiff[groupKey]) {
      const obj = {} as Record<UtKey, number>;
      for (const ut of UT_TYPES) obj[ut.key] = 0;
      machineTotalDiff[groupKey] = obj;
    }
    for (const ut of UT_TYPES) {
      const usages = entries.map(
        (e) => (isPerLineMode ? e?.values[ut.key]?.usagePerLine : e?.values[ut.key]?.usage) || 0
      );
      machineTotalDiff[groupKey][ut.key] += (usages[usages.length - 1] ?? 0) - (usages[0] ?? 0);
    }
  }

  const merged: AnalysisRow[] = allKeys.map((key) => {
    const entries = aggs.map((a) => a[key]);
    const first = entries.find((e) => e);
    const machine = first?.machine || key.split('||')[0];
    const main = first?.main || key.split('||')[1] || '';
    const line = first?.line || '';
    const groupKey = isPerLineMode ? key : machine;
    const row: AnalysisRow = {
      machine,
      main,
      process: first?.process || '',
      eqGroup: first?.eqGroup || '',
      subProcess: first?.subProcess || '',
      line,
      hasDiff: false,
      machineHasDiff: false,
      machineKey: groupKey,
      lineQty: entries.map((e) => e?.lineQty || 0),
      ut: {} as Record<UtKey, UtCellData>,
    };
    for (const ut of UT_TYPES) {
      let usage: number[];
      let unit: number[];
      let qty: number[];
      if (isPerLineMode) {
        usage = entries.map((e) => e?.values[ut.key]?.usagePerLine || 0);
        unit = entries.map(() => 0);
        qty = entries.map((e) => e?.values[ut.key]?.qtyPerLine || 0);
      } else {
        usage = entries.map((e) => e?.values[ut.key]?.usage || 0);
        unit = entries.map((e) => e?.values[ut.key]?.unit || 0);
        qty = entries.map((e) => e?.values[ut.key]?.qty || 0);
      }
      const diffU = usage[usage.length - 1] - usage[0];
      const diffQ = qty[qty.length - 1] - qty[0];
      const cell: UtCellData = { usage, unit, qty, diffU, diffQ };
      if (diffU !== 0) row.hasDiff = true;
      row.ut[ut.key] = cell;
    }
    // Machine(+Main) 전체 합계 기준으로 변화 있는지 판단
    row.machineHasDiff = Object.values(machineTotalDiff[groupKey]).some((v) => Math.abs(v) > 1e-9);
    return row;
  });

  const unmatchedEqGroups = isPerLineMode
    ? [
        ...new Set(
          merged
            .filter((r) => lineQtyMaps.some((m) => !m[r.eqGroup]))
            .map((r) => r.eqGroup)
            .filter(Boolean)
        ),
      ]
    : [];

  return { rows: merged, isPerLineMode, unmatchedEqGroups };
}

/** ⑤ 세부 Summary: 공정/설비군/세부공정별 UT 사용량(버전별) 집계 */
export interface DetailGroup {
  process: string;
  eqGroup: string;
  subProcess: string;
  ut: Record<UtKey, { usage: number[] }>;
}

/**
 * @param procOrder 공정 표시 순서(공통코드 PRDN_PROC_CD 기준, `공정명 → 순번`).
 *   비워 두면 공정도 이름순이 된다 — 호출부가 usePrdnProcOrder()로 받아 넘긴다.
 */
export function aggregateByProcessEqGroup(
  rows: AnalysisRow[],
  n: number,
  procOrder: Map<string, number> = new Map()
): DetailGroup[] {
  const map: Record<string, DetailGroup> = {};
  for (const r of rows) {
    const key = `${r.process || '(공정없음)'}||${r.eqGroup || '(설비군없음)'}||${
      r.subProcess || ''
    }`;
    if (!map[key]) {
      const ut = {} as DetailGroup['ut'];
      for (const t of UT_TYPES) ut[t.key] = { usage: Array(n).fill(0) };
      map[key] = {
        process: r.process || '',
        eqGroup: r.eqGroup || '',
        subProcess: r.subProcess || '',
        ut,
      };
    }
    for (const t of UT_TYPES) {
      const d = r.ut[t.key];
      if (d) d.usage.forEach((v, i) => (map[key].ut[t.key].usage[i] += v));
    }
  }
  // 공정은 공통코드 순서, 설비군·세부공정은 이름순 (④ 표와 같은 규칙)
  return sortByProcessGroup(Object.values(map), procOrder);
}

/** UT별 버전별 전체 합계 */
export function calcTotals(rows: AnalysisRow[], n: number): Record<UtKey, number[]> {
  const totals = {} as Record<UtKey, number[]>;
  for (const ut of UT_TYPES) totals[ut.key] = Array(n).fill(0);
  for (const r of rows) {
    for (const ut of UT_TYPES) {
      const d = r.ut[ut.key];
      if (d) d.usage.forEach((v, i) => (totals[ut.key][i] += v));
    }
  }
  return totals;
}
